/**
 * Address parsing and formatting utilities
 * 
 * Provides comprehensive functions for address manipulation, validation, and formatting.
 * Includes smart address parsing, validation, sanitization, and formatting with
 * consistent styling and abbreviations.
 * 
 * @author Atlas Team
 * @version 1.0.0
 * @since 2024
 * 
 * @example
 * ```typescript
 * import { parseAddress, formatAddressFromComponents, sanitizeAddress } from './addressUtils';
 * 
 * const parsed = parseAddress("123 Main Street");
 * const formatted = formatAddressFromComponents(parsed.components);
 * const clean = sanitizeAddress("  123 Main St  ");
 * ```
 */

/**
 * Structured address components
 * 
 * Represents the parsed components of an address with optional fields
 * for different address elements.
 */
export interface AddressComponents {
  /** House or building number */
  houseNumber?: string;
  /** Street or road name */
  road?: string;
  /** Intersection name (e.g., "Main St & Oak Ave") */
  intersection?: string;
  /** Landmark or point of interest */
  landmark?: string;
  /** Suburb or neighborhood */
  suburb?: string;
  /** City or locality */
  city?: string;
  /** State or province */
  state?: string;
  /** Country */
  country?: string;
  /** Postal or ZIP code */
  postalCode?: string;
}

/**
 * Parsed address result with confidence scoring
 * 
 * Contains the structured components of a parsed address along with
 * formatting results and confidence assessment.
 */
export interface ParsedAddress {
  /** Structured address components */
  components: AddressComponents;
  /** Formatted address string */
  formatted: string;
  /** Whether the address is valid and meaningful */
  isValid: boolean;
  /** Confidence level of the parsing result */
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Parse address string into structured components
 * 
 * Analyzes an address string and extracts structured components with confidence scoring.
 * Uses intelligent parsing to identify house numbers, road names, intersections, and landmarks.
 * 
 * @param address - The address string to parse
 * @returns ParsedAddress object with components, formatting, and confidence
 * 
 * @example
 * ```typescript
 * const parsed = parseAddress("123 Main Street");
 * console.log(parsed.components.houseNumber); // "123"
 * console.log(parsed.components.road); // "Main Street"
 * console.log(parsed.confidence); // "high"
 * ```
 * 
 * @remarks
 * - Handles various address formats and styles
 * - Provides confidence scoring based on parsing success
 * - Falls back to generic "Meeting Point" for invalid addresses
 * - Validates and sanitizes input before parsing
 */
export function parseAddress(address: string): ParsedAddress {
  if (!address || typeof address !== 'string') {
    return {
      components: {},
      formatted: 'Meeting Point',
      isValid: false,
      confidence: 'low'
    };
  }

  const components: AddressComponents = {};
  let confidence: 'high' | 'medium' | 'low' = 'low';
  let isValid = false;

  // Clean the address first
  const cleaned = sanitizeAddress(address);
  if (!cleaned) {
    return {
      components: {},
      formatted: 'Meeting Point',
      isValid: false,
      confidence: 'low'
    };
  }

  // Try to extract house number and street
  const houseNumberMatch = cleaned.match(/^(\d+[a-zA-Z]?)\s+(.+)$/);
  if (houseNumberMatch) {
    components.houseNumber = houseNumberMatch[1];
    components.road = houseNumberMatch[2];
    confidence = 'high';
    isValid = true;
  }
  // Try to extract intersection
  else if (cleaned.includes(' & ')) {
    components.intersection = cleaned;
    confidence = 'medium';
    isValid = true;
  }
  // Try to extract just road name
  else if (isValidRoadName(cleaned)) {
    components.road = cleaned;
    confidence = 'medium';
    isValid = true;
  }
  // Try to extract landmark
  else if (cleaned.includes('(') && cleaned.includes(')')) {
    const landmarkMatch = cleaned.match(/^(.+?)\s*\((.+?)\)$/);
    if (landmarkMatch) {
      components.landmark = landmarkMatch[1].trim();
      components.road = landmarkMatch[2].trim();
      confidence = 'medium';
      isValid = true;
    }
  }

  // Format the address based on components
  const formatted = formatAddressFromComponents(components);

  return {
    components,
    formatted,
    isValid,
    confidence
  };
}

/**
 * Format address from structured components
 * 
 * Converts structured address components into a human-readable address string
 * using a priority-based formatting system.
 * 
 * @param components - Structured address components
 * @returns Formatted address string
 * 
 * @example
 * ```typescript
 * const components = {
 *   houseNumber: "123",
 *   road: "Main Street"
 * };
 * const formatted = formatAddressFromComponents(components);
 * console.log(formatted); // "123 Main St"
 * ```
 * 
 * @remarks
 * - Uses 6-level priority system for formatting
 * - Applies consistent road name abbreviations
 * - Handles intersections, landmarks, and suburbs
 * - Falls back to "Meeting Point" for empty components
 */
export function formatAddressFromComponents(components: AddressComponents): string {
  // House number + road (highest priority)
  if (components.houseNumber && components.road) {
    return `${components.houseNumber} ${formatRoadName(components.road)}`;
  }

  // Intersection
  if (components.intersection) {
    return formatIntersection(components.intersection);
  }

  // Landmark with road
  if (components.landmark && components.road) {
    return `${components.landmark} (${formatRoadName(components.road)})`;
  }

  // Road with suburb
  if (components.road && components.suburb) {
    return `${formatRoadName(components.road)} (${components.suburb})`;
  }

  // Just road
  if (components.road) {
    return formatRoadName(components.road);
  }

  // Just landmark
  if (components.landmark) {
    return components.landmark;
  }

  return 'Meeting Point';
}

/**
 * Format road name with consistent styling
 */
export function formatRoadName(road: string): string {
  if (!road) return '';

  // Standardize common road type abbreviations
  const roadTypes: Record<string, string> = {
    'street': 'St',
    'avenue': 'Ave',
    'road': 'Rd',
    'drive': 'Dr',
    'lane': 'Ln',
    'court': 'Ct',
    'place': 'Pl',
    'boulevard': 'Blvd',
    'highway': 'Hwy',
    'parkway': 'Pkwy',
    'circle': 'Cir',
    'square': 'Sq',
    'terrace': 'Ter',
    'crescent': 'Cres',
    'close': 'Cl',
    'way': 'Way',
    'walk': 'Walk',
    'path': 'Path',
    'trail': 'Trl'
  };

  let formatted = road.trim();

  // Apply standard abbreviations
  Object.entries(roadTypes).forEach(([full, abbrev]) => {
    const regex = new RegExp(`\\b${full}\\b`, 'gi');
    formatted = formatted.replace(regex, abbrev);
  });

  // Capitalize first letter of each word
  formatted = formatted
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return formatted;
}

/**
 * Format intersection names
 */
export function formatIntersection(intersection: string): string {
  if (!intersection) return '';

  return intersection
    .replace(/\s*&\s*/g, ' & ') // Standardize ampersand spacing
    .replace(/\s+/g, ' ') // Remove extra spaces
    .trim();
}

/**
 * Sanitize and validate address string
 * 
 * Cleans and validates an address string by removing dangerous characters,
 * normalizing whitespace, and applying comprehensive validation rules.
 * 
 * @param address - Address string to sanitize
 * @returns Sanitized address string or null if invalid
 * 
 * @example
 * ```typescript
 * const clean = sanitizeAddress("  123 Main St  ");
 * console.log(clean); // "123 Main St"
 * 
 * const invalid = sanitizeAddress("near somewhere");
 * console.log(invalid); // null
 * ```
 * 
 * @remarks
 * - Removes dangerous special characters
 * - Normalizes whitespace and formatting
 * - Validates length (1-100 characters)
 * - Requires meaningful content (letters, not just numbers)
 * - Filters generic location words
 * - Prevents circular "meeting point" references
 */
export function sanitizeAddress(address: string): string | null {
  if (!address || typeof address !== 'string') return null;

  // Sanitize the address
  let sanitized = address
    .trim() // Remove leading/trailing whitespace
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/[^\w\s\-&.,()]/g, '') // Remove special characters except common address chars
    .trim();

  // Validation checks
  if (sanitized.length === 0) return null;
  if (sanitized.length > 100) return null; // Too long
  if (/^\d+$/.test(sanitized)) return null; // Just numbers
  if (/^(near|at|by|around)\s+/i.test(sanitized)) return null; // Generic location words
  if (sanitized.toLowerCase().includes('meeting point')) return null; // Avoid circular references

  // Check for minimum meaningful content
  const words = sanitized.split(/\s+/).filter(word => word.length > 1);
  if (words.length < 1) return null;

  // Ensure it's not just punctuation or special characters
  const hasLetters = /[a-zA-Z]/.test(sanitized);
  if (!hasLetters) return null;

  return sanitized;
}

/**
 * Check if a string is a valid road name
 */
export function isValidRoadName(road: string): boolean {
  if (!road || typeof road !== 'string') return false;

  const sanitized = sanitizeAddress(road);
  if (!sanitized) return false;

  // Must contain at least one letter
  if (!/[a-zA-Z]/.test(sanitized)) return false;

  // Must not be just numbers
  if (/^\d+$/.test(sanitized)) return false;

  // Must not be generic location words
  if (/^(near|at|by|around)\s+/i.test(sanitized)) return false;

  // Must have at least 2 characters
  if (sanitized.length < 2) return false;

  return true;
}

/**
 * Extract address components from Google Maps response
 */
export function extractGoogleComponents(googleData: any): AddressComponents {
  const components: AddressComponents = {};
  const result = googleData.results?.[0];

  if (!result) return components;

  // Parse address components from Google's structured format
  if (result.address_components) {
    result.address_components.forEach((component: any) => {
      const types = component.types;
      const longName = component.long_name;
      const shortName = component.short_name;

      if (types.includes('street_number')) {
        components.houseNumber = longName;
      } else if (types.includes('route')) {
        components.road = longName;
      } else if (types.includes('intersection')) {
        components.intersection = longName;
      } else if (types.includes('sublocality') || types.includes('neighborhood')) {
        components.suburb = longName;
      } else if (types.includes('locality')) {
        components.city = longName;
      } else if (types.includes('administrative_area_level_1')) {
        components.state = shortName;
      } else if (types.includes('country')) {
        components.country = shortName;
      } else if (types.includes('postal_code')) {
        components.postalCode = longName;
      }
    });
  }

  return components;
}

/**
 * Compare two addresses for similarity
 */
export function compareAddresses(address1: string, address2: string): number {
  if (!address1 || !address2) return 0;

  const parsed1 = parseAddress(address1);
  const parsed2 = parseAddress(address2);

  if (!parsed1.isValid || !parsed2.isValid) return 0;

  let similarity = 0;
  let totalComponents = 0;

  const components1 = parsed1.components;
  const components2 = parsed2.components;

  // Compare house numbers
  if (components1.houseNumber && components2.houseNumber) {
    totalComponents++;
    if (components1.houseNumber === components2.houseNumber) {
      similarity += 1;
    }
  }

  // Compare roads
  if (components1.road && components2.road) {
    totalComponents++;
    if (components1.road.toLowerCase() === components2.road.toLowerCase()) {
      similarity += 1;
    }
  }

  // Compare intersections
  if (components1.intersection && components2.intersection) {
    totalComponents++;
    if (components1.intersection.toLowerCase() === components2.intersection.toLowerCase()) {
      similarity += 1;
    }
  }

  // Compare landmarks
  if (components1.landmark && components2.landmark) {
    totalComponents++;
    if (components1.landmark.toLowerCase() === components2.landmark.toLowerCase()) {
      similarity += 1;
    }
  }

  return totalComponents > 0 ? similarity / totalComponents : 0;
}

/**
 * Get address confidence score
 */
export function getAddressConfidence(address: string): number {
  const parsed = parseAddress(address);
  
  if (!parsed.isValid) return 0;

  let score = 0;

  // Base score from confidence level
  switch (parsed.confidence) {
    case 'high': score += 0.8; break;
    case 'medium': score += 0.5; break;
    case 'low': score += 0.2; break;
  }

  // Bonus for having house number
  if (parsed.components.houseNumber) score += 0.1;

  // Bonus for having road name
  if (parsed.components.road) score += 0.1;

  // Penalty for very short addresses
  if (address.length < 5) score -= 0.2;

  // Penalty for very long addresses
  if (address.length > 50) score -= 0.1;

  return Math.max(0, Math.min(1, score));
}

/**
 * Detect if coordinates are at a road intersection
 */
export function detectIntersection(coordinates: [number, number], osmData?: any): boolean {
  if (!osmData || !osmData.elements) return false;

  // Look for multiple roads near the coordinates
  const nearbyRoads = osmData.elements.filter((element: any) => {
    if (element.type !== 'way' || !element.tags) return false;
    
    // Check if it's a road
    const highway = element.tags.highway;
    if (!highway || highway === 'footway' || highway === 'cycleway' || highway === 'path') {
      return false;
    }

    // Check if coordinates are near this road
    return isNearRoad(coordinates, element);
  });

  // If there are 2 or more roads nearby, it's likely an intersection
  return nearbyRoads.length >= 2;
}

/**
 * Check if coordinates are near a road way
 */
function isNearRoad(coordinates: [number, number], roadWay: any): boolean {
  if (!roadWay.geometry) return false;

  // Calculate distance to each segment of the road
  const [lng, lat] = coordinates;
  
  for (let i = 0; i < roadWay.geometry.length - 1; i++) {
    const segmentStart = roadWay.geometry[i];
    const segmentEnd = roadWay.geometry[i + 1];
    
    const distance = distanceToLineSegment(
      [lng, lat],
      [segmentStart.lon, segmentStart.lat],
      [segmentEnd.lon, segmentEnd.lat]
    );
    
    // If within 50 meters of any road segment, consider it near
    if (distance <= 0.0005) { // Approximately 50 meters in degrees
      return true;
    }
  }
  
  return false;
}

/**
 * Calculate distance from point to line segment
 */
function distanceToLineSegment(
  point: [number, number],
  lineStart: [number, number],
  lineEnd: [number, number]
): number {
  const [px, py] = point;
  const [x1, y1] = lineStart;
  const [x2, y2] = lineEnd;

  // Vector from line start to end
  const dx = x2 - x1;
  const dy = y2 - y1;
  
  // If line segment is a point, return distance to that point
  if (dx === 0 && dy === 0) {
    return Math.sqrt((px - x1) ** 2 + (py - y1) ** 2);
  }

  // Calculate parameter t for closest point on line segment
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy)));
  
  // Closest point on line segment
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;
  
  // Distance to closest point
  return Math.sqrt((px - closestX) ** 2 + (py - closestY) ** 2);
}

/**
 * Extract intersection name from nearby roads
 */
export function extractIntersectionName(coordinates: [number, number], osmData?: any): string | null {
  if (!osmData || !osmData.elements) return null;

  const nearbyRoads = osmData.elements.filter((element: any) => {
    if (element.type !== 'way' || !element.tags) return false;
    
    const highway = element.tags.highway;
    if (!highway || highway === 'footway' || highway === 'cycleway' || highway === 'path') {
      return false;
    }

    return isNearRoad(coordinates, element);
  });

  if (nearbyRoads.length < 2) return null;

  // Get road names, prioritizing named roads
  const roadNames = nearbyRoads
    .map((road: any) => {
      const name = road.tags.name || road.tags.ref || road.tags.highway;
      return name ? formatRoadName(name) : null;
    })
    .filter((name: string | null) => name && name.length > 0)
    .slice(0, 2); // Take first two named roads

  if (roadNames.length >= 2) {
    return `${roadNames[0]} & ${roadNames[1]}`;
  } else if (roadNames.length === 1) {
    return `${roadNames[0]} & Road`;
  }

  return null;
}

/**
 * Enhanced intersection formatting with road name detection
 */
export function formatIntersectionWithDetection(
  intersection: string, 
  coordinates?: [number, number], 
  osmData?: any
): string {
  if (!intersection) return '';

  // If we have coordinates and OSM data, try to detect intersection name
  if (coordinates && osmData) {
    const detectedName = extractIntersectionName(coordinates, osmData);
    if (detectedName) {
      return detectedName;
    }
  }

  // Fall back to formatting the provided intersection string
  return formatIntersection(intersection);
}

/**
 * Check if an address represents an intersection
 */
export function isIntersectionAddress(address: string): boolean {
  if (!address) return false;

  const lowerAddress = address.toLowerCase();
  
  // Check for common intersection indicators
  const intersectionIndicators = [
    ' & ',
    ' and ',
    ' at ',
    ' intersection',
    ' crossing',
    ' junction'
  ];

  return intersectionIndicators.some(indicator => lowerAddress.includes(indicator));
}

/**
 * Extract road names from intersection string
 */
export function extractRoadsFromIntersection(intersection: string): string[] {
  if (!intersection) return [];

  // Split by common intersection separators
  const separators = [' & ', ' and ', ' at ', ' intersection', ' crossing', ' junction'];
  let roads = [intersection];

  for (const separator of separators) {
    const newRoads: string[] = [];
    for (const road of roads) {
      if (road.includes(separator)) {
        newRoads.push(...road.split(separator).map(r => r.trim()));
      } else {
        newRoads.push(road);
      }
    }
    roads = newRoads;
  }

  // Clean and format road names
  return roads
    .map(road => road.trim())
    .filter(road => road.length > 0)
    .map(road => formatRoadName(road))
    .filter(road => isValidRoadName(road));
}
